import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Trophy, Coins, ShieldAlert, Settings2, Gift, PlusCircle, MinusCircle, Save, Check, X, User, Zap } from 'lucide-react';

const TIER_COLORS = {
  bronze: 'bg-orange-500/15 text-orange-700 dark:text-orange-400',
  silver: 'bg-slate-400/20 text-slate-600 dark:text-slate-300',
  gold: 'bg-amber-500/15 text-amber-700 dark:text-amber-500',
  platinum: 'bg-purple-500/15 text-purple-700 dark:text-purple-400',
};

const NumberField = ({ label, value, onChange, testId, suffix }) => (
  <div>
    <Label className='text-xs'>{label}</Label>
    <div className='flex items-center gap-2'>
      <Input
        type='number'
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className='h-9'
        data-testid={testId}
      />
      {suffix && <span className='text-xs text-muted-foreground shrink-0'>{suffix}</span>}
    </div>
  </div>
);

const AdminReferrals = () => {
  const [settings, setSettings] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [fraudQueue, setFraudQueue] = useState([]);
  const [pointRules, setPointRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ user_id: '', direction: 'credit', amount: '', note: '' });

  const load = async () => {
    try {
      const [s, r, lb, fq, pr] = await Promise.all([
        api.get('/admin/referrals/settings').then(x => x.data),
        api.get('/referrals/rewards').then(x => x.data),
        api.get('/admin/referrals/leaderboard').then(x => x.data),
        api.get('/admin/referrals/fraud-queue').then(x => x.data),
        api.get('/admin/points/rules').then(x => x.data).catch(() => []),
      ]);
      setSettings(s);
      setPointRules(pr);
      setRewards(r);
      setLeaderboard(lb);
      setFraudQueue(fq);
    } catch (e) {
      toast.error('Failed to load referral admin data');
    }
  };

  useEffect(() => { load(); }, []);

  const patch = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));
  const patchTier = (k, v) => setSettings((prev) => ({ ...prev, tier_thresholds: { ...(prev.tier_thresholds || {}), [k]: v } }));
  const patchTierBonus = (k, v) => setSettings((prev) => ({ ...prev, tier_bonus_pct: { ...(prev.tier_bonus_pct || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/referrals/settings', settings);
      setSettings(data);
      toast.success('Referral settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveReward = async (r) => {
    try {
      const payload = {
        name: r.name,
        active: !!r.active,
        display_order: Number(r.display_order) || 0,
      };
      if (r.derived_pricing) {
        payload.base_price_inr = Number(r.base_price_inr);
      } else {
        payload.cost_points = Number(r.cost_points);
      }
      await api.patch(`/admin/referrals/rewards/${r.id}`, payload);
      toast.success('Reward updated');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const moderate = async (id, action) => {
    if (!confirm(`${action === 'approve' ? 'Approve' : 'Block'} this referral?`)) return;
    try {
      await api.post(`/admin/referrals/${id}/moderate`, { action });
      toast.success(`Referral ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  const applyManual = async () => {
    const amount = Number(manualForm.amount);
    if (!manualForm.user_id.trim() || !amount || amount <= 0 || !manualForm.note.trim()) {
      return toast.error('User ID, a positive amount, and a note are required');
    }
    const delta = manualForm.direction === 'credit' ? amount : -amount;
    try {
      const { data } = await api.post('/admin/referrals/points/manual', {
        user_id: manualForm.user_id.trim(),
        delta,
        note: manualForm.note.trim(),
      });
      toast.success(`${manualForm.direction === 'credit' ? 'Credited' : 'Debited'} ${amount} pts. New balance: ${data.balance}`);
      setManualForm({ user_id: '', direction: 'credit', amount: '', note: '' });
      setManualOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Adjustment failed');
    }
  };

  if (!settings) return <div className='py-10 text-sm text-muted-foreground'>Loading…</div>;

  return (
    <div className='space-y-4' data-testid='admin-referrals-page'>
      <div className='flex items-start justify-between flex-wrap gap-3'>
        <div>
          <h1 className='text-2xl font-semibold tracking-tight flex items-center gap-2'>
            <Gift className='h-6 w-6 text-primary' /> Referrals & Rewards
          </h1>
          <p className='text-sm text-muted-foreground'>Configure tiers, points and rewards. Review fraud queue and reward top referrers.</p>
        </div>
        <div className='flex items-center gap-2'>
          <div className='flex items-center gap-2 rounded-md border px-3 h-9'>
            <Switch checked={!!settings.enabled} onCheckedChange={(v) => patch('enabled', v)} data-testid='ref-enabled-switch' />
            <span className='text-xs font-medium'>{settings.enabled ? 'Enabled' : 'Disabled'}</span>
          </div>
          <Button onClick={save} disabled={saving} data-testid='ref-save-settings-btn'>
            <Save className='h-4 w-4 mr-1.5' /> {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue='settings' className='mt-2'>
        <TabsList>
          <TabsTrigger value='settings' data-testid='tab-ref-settings'><Settings2 className='h-4 w-4 mr-1.5' /> Settings</TabsTrigger>
          <TabsTrigger value='rewards' data-testid='tab-ref-rewards'><Gift className='h-4 w-4 mr-1.5' /> Rewards catalog</TabsTrigger>
          <TabsTrigger value='rules' data-testid='tab-ref-earning-rules'><Zap className='h-4 w-4 mr-1.5' /> Earning rules</TabsTrigger>
          <TabsTrigger value='leaderboard' data-testid='tab-ref-leaderboard'><Trophy className='h-4 w-4 mr-1.5' /> Leaderboard</TabsTrigger>
          <TabsTrigger value='fraud' data-testid='tab-ref-fraud'>
            <ShieldAlert className='h-4 w-4 mr-1.5' /> Fraud queue {fraudQueue.length > 0 && <Badge className='ml-1' variant='destructive'>{fraudQueue.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value='manual' data-testid='tab-ref-manual'><Coins className='h-4 w-4 mr-1.5' /> Manual adjust</TabsTrigger>
        </TabsList>

        {/* SETTINGS */}
        <TabsContent value='settings' className='space-y-4 mt-4'>
          <Card className='p-5 space-y-4'>
            <div>
              <div className='font-semibold mb-2'>Stage rewards (points)</div>
              <div className='grid md:grid-cols-3 gap-4'>
                <div className='p-3 border rounded-md space-y-2'>
                  <div className='text-xs uppercase text-muted-foreground'>Stage 1 — Signup</div>
                  <NumberField label='Referrer pts' value={settings.stage1_referrer_pts} onChange={(v) => patch('stage1_referrer_pts', v)} testId='ref-s1-referrer' />
                  <NumberField label='Referee pts' value={settings.stage1_referee_pts} onChange={(v) => patch('stage1_referee_pts', v)} testId='ref-s1-referee' />
                </div>
                <div className='p-3 border rounded-md space-y-2'>
                  <div className='text-xs uppercase text-muted-foreground'>Stage 2 — First ad</div>
                  <NumberField label='Referrer pts' value={settings.stage2_referrer_pts} onChange={(v) => patch('stage2_referrer_pts', v)} testId='ref-s2-referrer' />
                  <NumberField label='Referee pts' value={settings.stage2_referee_pts} onChange={(v) => patch('stage2_referee_pts', v)} testId='ref-s2-referee' />
                </div>
                <div className='p-3 border rounded-md space-y-2'>
                  <div className='text-xs uppercase text-muted-foreground'>Stage 3 — First payment</div>
                  <NumberField label='Referrer pts' value={settings.stage3_referrer_pts} onChange={(v) => patch('stage3_referrer_pts', v)} testId='ref-s3-referrer' />
                  <NumberField label='Referee pts' value={settings.stage3_referee_pts} onChange={(v) => patch('stage3_referee_pts', v)} testId='ref-s3-referee' />
                </div>
              </div>
            </div>

            <div>
              <div className='font-semibold mb-2'>Conversion economics</div>
              <div className='grid md:grid-cols-3 gap-4'>
                <NumberField label='Points per ₹1 value' value={settings.points_per_inr} onChange={(v) => patch('points_per_inr', v)} testId='ref-points-per-inr' suffix='pts = ₹1' />
                <NumberField label='Referee first-purchase coupon' value={settings.referee_first_purchase_coupon_percent} onChange={(v) => patch('referee_first_purchase_coupon_percent', v)} testId='ref-referee-coupon' suffix='%' />
                <NumberField label='Welcome bonus (referee)' value={settings.welcome_bonus_pts} onChange={(v) => patch('welcome_bonus_pts', v)} testId='ref-welcome-bonus' suffix='pts' />
              </div>
            </div>

            <div>
              <div className='font-semibold mb-2'>Referrer tiers</div>
              <div className='grid md:grid-cols-4 gap-3'>
                {['bronze','silver','gold','platinum'].map(t => (
                  <Card key={t} className={`p-3 space-y-2 border ${TIER_COLORS[t]}`}>
                    <div className='flex items-center gap-1.5 font-semibold capitalize'><Trophy className='h-4 w-4' />{t}</div>
                    <NumberField label='Paying referrals to unlock' value={settings.tier_thresholds?.[t]} onChange={(v) => patchTier(t, v)} testId={`ref-tier-${t}-threshold`} />
                    <NumberField label='Bonus % on future points' value={settings.tier_bonus_pct?.[t]} onChange={(v) => patchTierBonus(t, v)} testId={`ref-tier-${t}-bonus`} suffix='%' />
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <div className='font-semibold mb-2'>Fraud controls</div>
              <div className='grid md:grid-cols-3 gap-4'>
                <NumberField label='Max accounts per GST' value={settings.same_gst_max_accounts} onChange={(v) => patch('same_gst_max_accounts', v)} testId='ref-max-gst' />
                <NumberField label='Max open referrals per user' value={settings.per_user_max_open_referrals} onChange={(v) => patch('per_user_max_open_referrals', v)} testId='ref-max-open' />
                <NumberField label='Same-IP lookback' value={settings.same_ip_lookback_days} onChange={(v) => patch('same_ip_lookback_days', v)} testId='ref-ip-lookback' suffix='days' />
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* REWARDS */}
        <TabsContent value='rewards' className='space-y-3 mt-4'>
          <div className='text-xs text-muted-foreground'>Plan-linked rewards derive their point cost automatically from the plan base price (+GST − loyalty discount). Editing the base price here updates the plan everywhere: checkout, Razorpay, Pay-with-Points and this store.</div>
          <Card className='p-0 overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead className='text-left border-b bg-secondary'>
                <tr>
                  <th className='py-2 px-4'>Name</th>
                  <th className='py-2 px-4'>Category</th>
                  <th className='py-2 px-4 w-32'>Base price (₹)</th>
                  <th className='py-2 px-4 w-32'>Cost (pts)</th>
                  <th className='py-2 px-4 w-24'>Order</th>
                  <th className='py-2 px-4 w-24'>Active</th>
                  <th className='py-2 px-4 w-24'></th>
                </tr>
              </thead>
              <tbody>
                {rewards.map((r, i) => (
                  <tr key={r.id} className='border-b last:border-b-0' data-testid={`admin-reward-row-${r.id}`}>
                    <td className='py-2 px-4'>
                      <Input value={r.name} onChange={(e) => setRewards(rewards.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className='h-8' />
                    </td>
                    <td className='py-2 px-4'><Badge variant='outline' className='capitalize'>{r.category}</Badge></td>
                    <td className='py-2 px-4'>
                      {r.derived_pricing ? (
                        <Input
                          type='number'
                          value={r.base_price_inr ?? ''}
                          onChange={(e) => setRewards(rewards.map((x, j) => j === i ? { ...x, base_price_inr: Number(e.target.value) } : x))}
                          className='h-8'
                          data-testid={`reward-base-price-${r.id}`}
                        />
                      ) : (
                        <span className='text-xs text-muted-foreground'>—</span>
                      )}
                    </td>
                    <td className='py-2 px-4'>
                      {r.derived_pricing ? (
                        <div>
                          <div className='font-medium tabular-nums' data-testid={`reward-derived-cost-${r.id}`}>{Number(r.cost_points).toLocaleString('en-IN')}</div>
                          <div className='text-[10px] text-muted-foreground'>auto: price + GST − {r.loyalty_discount_pct}%</div>
                        </div>
                      ) : (
                        <Input type='number' value={r.cost_points} onChange={(e) => setRewards(rewards.map((x, j) => j === i ? { ...x, cost_points: Number(e.target.value) } : x))} className='h-8' />
                      )}
                    </td>
                    <td className='py-2 px-4'>
                      <Input type='number' value={r.display_order || 0} onChange={(e) => setRewards(rewards.map((x, j) => j === i ? { ...x, display_order: Number(e.target.value) } : x))} className='h-8' />
                    </td>
                    <td className='py-2 px-4'>
                      <Switch checked={!!r.active} onCheckedChange={(v) => setRewards(rewards.map((x, j) => j === i ? { ...x, active: v } : x))} />
                    </td>
                    <td className='py-2 px-4 text-right'>
                      <Button size='sm' variant='outline' onClick={() => saveReward(r)} data-testid={`save-reward-${r.id}`}>
                        <Save className='h-3.5 w-3.5' />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* EARNING RULES (Wave 1.5) */}
        <TabsContent value='rules' className='space-y-3 mt-4'>
          <div className='text-xs text-muted-foreground'>
            Declarative earning rules. Toggle any rule OFF to instantly kill its point payouts. Edit point value or monthly cap to tune the economy. Rules with <Badge variant='outline' className='text-[9px] mx-1'>Coming soon</Badge> are pre-configured for future features (Forums, Kaizen, Expert ranks) \u2014 activate them when those features launch.
          </div>
          <Card className='p-0 overflow-x-auto'>
            <table className='w-full text-sm' data-testid='point-rules-table'>
              <thead className='text-left border-b bg-secondary'>
                <tr>
                  <th className='py-2 px-3'>Rule</th>
                  <th className='py-2 px-3 w-28'>Category</th>
                  <th className='py-2 px-3 w-24 text-right'>Points</th>
                  <th className='py-2 px-3 w-28 text-right'>Monthly cap</th>
                  <th className='py-2 px-3 w-32'>Cooldown</th>
                  <th className='py-2 px-3 w-20'>Active</th>
                  <th className='py-2 px-3 w-24'></th>
                </tr>
              </thead>
              <tbody>
                {pointRules.map((r, i) => (
                  <tr key={r.id} className='border-b last:border-b-0' data-testid={`point-rule-row-${r.id}`}>
                    <td className='py-2 px-3'>
                      <div className='font-medium text-sm flex items-center gap-1.5'>
                        {r.label}
                        {r.coming_soon && <Badge variant='outline' className='text-[9px] uppercase h-4'>Coming soon</Badge>}
                        {r.exempt_from_global_cap && <Badge variant='outline' className='text-[9px] uppercase h-4 border-[hsl(var(--verified))]/40 text-[hsl(var(--verified))]'>Uncapped</Badge>}
                      </div>
                      <div className='text-[11px] text-muted-foreground'>{r.description}</div>
                    </td>
                    <td className='py-2 px-3'><Badge variant='outline' className='capitalize text-[10px]'>{r.category}</Badge></td>
                    <td className='py-2 px-3 text-right'>
                      {r.is_percentage ? (
                        <div className='text-right'>
                          <Input
                            type='number' value={r.percentage ?? 0} step='0.5'
                            onChange={(e) => setPointRules(pointRules.map((x, j) => j === i ? { ...x, percentage: Number(e.target.value) } : x))}
                            className='h-8 text-right w-20 ml-auto' data-testid={`rule-percentage-${r.id}`}
                          />
                          <div className='text-[10px] text-muted-foreground'>% of purchase</div>
                        </div>
                      ) : (
                        <Input
                          type='number' value={r.points}
                          onChange={(e) => setPointRules(pointRules.map((x, j) => j === i ? { ...x, points: Number(e.target.value) } : x))}
                          className='h-8 text-right w-20 ml-auto' data-testid={`rule-points-${r.id}`}
                        />
                      )}
                    </td>
                    <td className='py-2 px-3 text-right'>
                      <Input
                        type='number' value={r.monthly_cap || 0}
                        onChange={(e) => setPointRules(pointRules.map((x, j) => j === i ? { ...x, monthly_cap: Number(e.target.value) } : x))}
                        className='h-8 text-right w-20 ml-auto' data-testid={`rule-cap-${r.id}`}
                        disabled={r.exempt_from_global_cap}
                      />
                      <div className='text-[10px] text-muted-foreground'>{r.monthly_cap === 0 ? 'unlimited' : ''}</div>
                    </td>
                    <td className='py-2 px-3 text-[11px] text-muted-foreground'>{r.cooldown?.replace(/_/g, ' ')}</td>
                    <td className='py-2 px-3'>
                      <Switch
                        checked={!!r.active}
                        onCheckedChange={(v) => setPointRules(pointRules.map((x, j) => j === i ? { ...x, active: v } : x))}
                        data-testid={`rule-active-${r.id}`}
                      />
                    </td>
                    <td className='py-2 px-3 text-right'>
                      <Button
                        size='sm' variant='outline'
                        onClick={async () => {
                          try {
                            const payload = {
                              points: r.points,
                              active: r.active,
                              monthly_cap: r.monthly_cap,
                              coming_soon: r.coming_soon,
                              percentage: r.percentage,
                            };
                            await api.patch(`/admin/points/rules/${r.id}`, payload);
                            toast.success(`Saved: ${r.label}`);
                          } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
                        }}
                        data-testid={`save-rule-${r.id}`}
                      >
                        <Save className='h-3.5 w-3.5' />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>


        {/* LEADERBOARD */}
        <TabsContent value='leaderboard' className='space-y-3 mt-4'>
          {leaderboard.length === 0 ? (
            <div className='py-10 text-center text-muted-foreground text-sm'>No verified paying referrals yet.</div>
          ) : (
            <Card className='p-0 overflow-x-auto'>
              <table className='w-full text-sm' data-testid='ref-leaderboard-table'>
                <thead className='text-left border-b bg-secondary'>
                  <tr>
                    <th className='py-2 px-4 w-12'>#</th>
                    <th className='py-2 px-4'>User</th>
                    <th className='py-2 px-4'>Referral code</th>
                    <th className='py-2 px-4 text-right'>Paying referrals</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((r, i) => (
                    <tr key={r._id} className='border-b last:border-b-0'>
                      <td className='py-2 px-4 font-semibold'>{i + 1}</td>
                      <td className='py-2 px-4'>
                        <div className='font-medium'>{r.user_name || '—'}</div>
                        <div className='text-xs text-muted-foreground'>{r.user_email}</div>
                      </td>
                      <td className='py-2 px-4'><code className='text-xs'>{r.referral_code || '—'}</code></td>
                      <td className='py-2 px-4 text-right font-semibold text-primary'>{r.paying}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>

        {/* FRAUD QUEUE */}
        <TabsContent value='fraud' className='space-y-3 mt-4'>
          {fraudQueue.length === 0 ? (
            <div className='py-10 text-center text-muted-foreground text-sm'>Nothing pending review. 🎉</div>
          ) : (
            <Card className='p-0 overflow-x-auto'>
              <table className='w-full text-sm' data-testid='ref-fraud-table'>
                <thead className='text-left border-b bg-secondary'>
                  <tr>
                    <th className='py-2 px-4'>Referrer</th>
                    <th className='py-2 px-4'>Referee</th>
                    <th className='py-2 px-4'>Stage</th>
                    <th className='py-2 px-4'>Flag reason</th>
                    <th className='py-2 px-4 text-right'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fraudQueue.map((r) => (
                    <tr key={r.id} className='border-b last:border-b-0'>
                      <td className='py-2 px-4'>
                        <div className='font-medium'>{r.referrer_user_id_name || '—'}</div>
                        <div className='text-xs text-muted-foreground'>{r.referrer_user_id_email}</div>
                      </td>
                      <td className='py-2 px-4'>
                        <div className='font-medium'>{r.referee_user_id_name || '—'}</div>
                        <div className='text-xs text-muted-foreground'>{r.referee_user_id_email}</div>
                      </td>
                      <td className='py-2 px-4 text-xs'>
                        {r.stage3_ts ? 'Payment' : r.stage2_ts ? 'First ad' : 'Signup'}
                      </td>
                      <td className='py-2 px-4 text-xs text-muted-foreground'>{r.flag_reason || 'auto-flagged'}</td>
                      <td className='py-2 px-4 text-right space-x-2'>
                        <Button size='sm' variant='outline' onClick={() => moderate(r.id, 'approve')} data-testid={`approve-referral-${r.id}`}>
                          <Check className='h-3.5 w-3.5 mr-1' /> Approve
                        </Button>
                        <Button size='sm' variant='destructive' onClick={() => moderate(r.id, 'block')} data-testid={`block-referral-${r.id}`}>
                          <X className='h-3.5 w-3.5 mr-1' /> Block
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>

        {/* MANUAL ADJUSTMENT */}
        <TabsContent value='manual' className='mt-4'>
          <Card className='p-5 max-w-2xl'>
            <div className='font-semibold mb-1 flex items-center gap-2'><Coins className='h-4 w-4' /> Manual points adjustment</div>
            <div className='text-xs text-muted-foreground mb-4'>Credit or debit points for a specific user. All actions are logged to the points ledger.</div>
            <div className='space-y-3'>
              <div>
                <Label className='text-xs'>User ID</Label>
                <Input value={manualForm.user_id} onChange={(e) => setManualForm({ ...manualForm, user_id: e.target.value })} placeholder='UUID of the user' data-testid='manual-user-id' />
              </div>
              <div>
                <Label className='text-xs'>Action</Label>
                <div className='grid grid-cols-2 gap-2' role='radiogroup' aria-label='Adjustment direction'>
                  <button
                    type='button'
                    role='radio'
                    aria-checked={manualForm.direction === 'credit'}
                    onClick={() => setManualForm({ ...manualForm, direction: 'credit' })}
                    className={`rounded-md border px-3 py-2 text-sm flex items-center justify-center gap-1.5 transition-colors ${manualForm.direction === 'credit' ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 font-semibold' : 'bg-background hover:bg-secondary'}`}
                    data-testid='manual-direction-credit'
                  >
                    <PlusCircle className='h-4 w-4' /> Credit (Add points)
                  </button>
                  <button
                    type='button'
                    role='radio'
                    aria-checked={manualForm.direction === 'debit'}
                    onClick={() => setManualForm({ ...manualForm, direction: 'debit' })}
                    className={`rounded-md border px-3 py-2 text-sm flex items-center justify-center gap-1.5 transition-colors ${manualForm.direction === 'debit' ? 'bg-red-500/10 border-red-500/40 text-red-700 font-semibold' : 'bg-background hover:bg-secondary'}`}
                    data-testid='manual-direction-debit'
                  >
                    <MinusCircle className='h-4 w-4' /> Debit (Remove points)
                  </button>
                </div>
              </div>
              <div>
                <Label className='text-xs'>Amount</Label>
                <Input
                  type='number'
                  min='1'
                  value={manualForm.amount}
                  onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })}
                  placeholder='e.g. 10'
                  data-testid='manual-amount'
                />
                {Number(manualForm.amount) > 0 && (
                  <div className={`text-xs mt-1 font-medium ${manualForm.direction === 'credit' ? 'text-emerald-700' : 'text-red-700'}`} data-testid='manual-preview'>
                    Preview: will {manualForm.direction === 'credit' ? 'credit' : 'debit'} {Number(manualForm.amount)} pts
                    <span className='text-muted-foreground font-normal'> (ledger delta: {manualForm.direction === 'credit' ? '+' : '-'}{Number(manualForm.amount)})</span>
                  </div>
                )}
              </div>
              <div>
                <Label className='text-xs'>Reason / note (required)</Label>
                <Textarea value={manualForm.note} onChange={(e) => setManualForm({ ...manualForm, note: e.target.value })} placeholder='Compensation for signup bug ticket #123' rows={2} data-testid='manual-note' />
              </div>
              <Button onClick={applyManual} data-testid='manual-apply-btn'>
                <PlusCircle className='h-4 w-4 mr-1.5' /> Apply adjustment
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReferrals;
